import { defineStore } from "pinia";
import { ref } from 'vue'

import { useUserStore } from "./user";

import { useActivityStore } from "./activity";
import { useNotificationStore } from './notification';

import { setKey as setBrowserStorageKey } from "./helpers/localStorage.mjs";
import { fetchFromBackend } from "./helpers/backend.mjs";




export const useBusinessPartnersStore = defineStore("businessPartners", {
  state: () => {
    return {
      businessPartnerLists: [], // a list of businessPartners defined by a specific filter (search)
      businessPartners: [], // single businessPartners with all their data
      lastVisited: {
        id: null,          // businessPartner id (or gpNo) to highlight when returning
        gpNo: null,        // optional gpNo for DOM id lookup
        page: null,        // page number user was on
        timestamp: null    // for stale cleanup if needed
      },
      lastVisitedHistory: [] // array of {id,gpNo,name,page,timestamp}
    }
  },
  getters: {
    businessPartnerList: (state) => {
      const userStore = useUserStore();
      const businessPartnerListExists = state.businessPartnerLists.find((list) => list.signature === userStore.currentSelection.businessPartnerList.signature);
      return (businessPartnerListExists) ? businessPartnerListExists : { summary: '', data: [], size: 0 };
    },
    businessPartnerListBySignature: (state) => (signature) => {
      const userStore = useUserStore();
      const businessPartnerListExists = state.businessPartnerLists.find((list) => list.signature === signature);
      return (businessPartnerListExists) ? businessPartnerListExists.data : [];
    },
    businessPartnerLeadLists: (state) => {
      const userStore = useUserStore();
      const lists = [];
      const listsInLeadSegments = [];
      const businessPartnerListExists = state.businessPartnerLists.find((list) => list.signature === `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#LeadList`);
      // console.log('businessPartnerListExists', businessPartnerListExists)
      if (!businessPartnerListExists) return []
      for (const segment of userStore.filterOption.businessPartnerSegment.filter((segment) => ['Leads', 'Sugar Aktionen'].includes(segment.name))) {
        for (const list of segment.lists) {
          listsInLeadSegments.push(list);
        }
      }
      for (const businessPartner of businessPartnerListExists.data) {
        for (const businessPartnerList of businessPartner.lists) {
          const knownList = listsInLeadSegments.find((list) => list.name === businessPartnerList);
          if (!knownList) continue
          const listExists = lists.find((list) => list.id === knownList.id);
          if (!listExists) {
            lists.push(Object.assign({}, knownList, { businessPartners: [businessPartner] }));
          } else {
            listExists.businessPartners.push(businessPartner);
          }
        }
      }
      // console.log('listsInLeadSegments', listsInLeadSegments, 'lists', lists)
      return lists
    },
    businessPartner: (state) => {
      const userStore = useUserStore();
      const businessPartnerExists = state.businessPartners.find((businessPartner) => businessPartner.signature === `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#${userStore.currentSelection.businessPartner.id}`);
      if (businessPartnerExists) {
        // Defensive Normalisierung der Kontakte (nur auf Kopie, nicht auf reaktivem Objekt!)
        const rawContacts = businessPartnerExists?.data?.contacts;
        const contactsArray = Array.isArray(rawContacts)
          ? rawContacts
          : (rawContacts ? [rawContacts] : []);
        // Kopie für Normalisierung erzeugen
        const dataCopy = JSON.parse(JSON.stringify(businessPartnerExists.data));
        dataCopy.contacts = contactsArray.map((contact) => {
          const c = { ...contact };
          // Telefonnummern normalisieren
          if (Array.isArray(c.phoneNumbers)) {
            if (c.phoneMobile === undefined) {
              const m = c.phoneNumbers.find(p => p.type === 'mobile');
              c.phoneMobile = m ? (m.number || m.name || null) : null;
            }
            if (c.phoneStationary === undefined) {
              const w = c.phoneNumbers.find(p => ['work', 'stationary'].includes(p.type));
              c.phoneStationary = w ? (w.number || w.name || null) : null;
            }
          } else {
            if (c.phoneMobile === undefined) c.phoneMobile = null;
            if (c.phoneStationary === undefined) c.phoneStationary = null;
          }
          // E-Mails normalisieren
          if ((!c.emailList || c.emailList.length === 0) && Array.isArray(c.emailAddresses) && c.emailAddresses.length > 0) {
            c.emailList = c.emailAddresses.map(e => ({ name: typeof e === 'string' ? e : (e?.name || '') }));
          }
          if ((!c.emailList || c.emailList.length === 0) && c.email) {
            c.emailList = [{ name: c.email }];
          }
          if (!c.emailList || c.emailList.length === 0 || !c.emailList[0].name) {
            c.emailList = [{ name: '-- unbekannt --' }];
          }
          const first = c.emailList?.[0]?.name || '';
          c.email = (c.email && c.email.trim() !== '')
            ? c.email
            : ((first && first !== '-- unbekannt --') ? first : '');
          return c;
        });
        // Return expanded businessPartner with kstLinks and computed fields (nur auf Kopie!)
        return formatBusinessPartner(dataCopy);
      }
      return null;
    },
    reminders: (state) => {
      const userStore = useUserStore();
      const reminders = {
        'Wiedervorlagen': [],
        'VorOrtTermine': []
      }
      for (const type of ['Wiedervorlagen', 'VorOrtTermine']) {
        const list = state.businessPartnerLists.find((list) => list.signature === `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Reminder:${type}`);
        // console.log('reminder list', list)
        if (!list || list.length === 0) continue
        for (const businessPartner of list.data) {
          const remainingMinutes = parseInt((new Date(businessPartner.salesPitch.dateFollowUp) - Date.now()) / (1000 * 60))
          // console.log('reminder businessPartner', businessPartner.name, remainingMinutes, 'in List', type)
          // if (remainingMinutes < 600 && remainingMinutes > -15 && !reminders[type].find((reminder) => reminder.businessPartner.id === businessPartner.id)) {
          //   reminders[type].push({
          //     businessPartner: businessPartner,
          //     remainingMinutes: remainingMinutes,
          //     noAppointment: false,
          //     missed: remainingMinutes < 0
          //   })
          // }
          if (!reminders[type].find((reminder) => reminder.businessPartner.id === businessPartner.id)) {
            reminders[type].push({
              businessPartner: businessPartner,
              remainingMinutes: remainingMinutes,
              noAppointment: false,
              missed: remainingMinutes < 0
            })
          }
        }
      }
      reminders['VorOrtTermine'].sort((a, b) => a.remainingMinutes - b.remainingMinutes)
      reminders['Wiedervorlagen'].sort((a, b) => a.remainingMinutes - b.remainingMinutes)
      return reminders
    },
    namedFilter: (state) => (name) => {
      const userStore = useUserStore();
      // console.log('requesting namedFilter', name)
      let filter = {
        idCompany: userStore.currentSelection.campaign.company.id,
        idCampaign: userStore.currentSelection.campaign.id,
        idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
        idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
      };
      switch (name) {
        case 'Today:Wiedervorlagen': {
          // Immer einen frischen, vordefinierten Filter ohne UI-Übernahme
          const today = new Date().toISOString().split('T')[0];
          filter = {
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
            page: 1,
            pages: 1,
            pageSize: 2500,
            id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 2, dateStart: today, dateEnd: today },
            idBusinessPartnerOrder: 3,
            dateStart: today,
            dateEnd: today,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:Wiedervorlagen`,
            summary: 'Today:Wiedervorlagen'
          };
          break;
        }
        case 'Today:VorOrtTermine': {
          // Immer einen frischen, vordefinierten Filter ohne UI-Übernahme
          const today = new Date().toISOString().split('T')[0];
          filter = {
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
            idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
            page: 1,
            pages: 1,
            pageSize: 2500,
            id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 3, dateStart: today, dateEnd: today },
            idBusinessPartnerOrder: 3,
            dateStart: today,
            dateEnd: today,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Today:VorOrtTermine`,
            summary: 'Today:VorOrtTermine'
          };
          break;
        }
        case 'Reminder:Wiedervorlagen':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 3,
            id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 2, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: 3,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Reminder:Wiedervorlagen`,
            summary: 'Reminder:Wiedervorlagen'
          });
          break;
        case 'Reminder:VorOrtTermine':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 3,
            id: -1,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 3, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: 3,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#Reminder:VorOrtTermine`,
            summary: 'Reminder:VorOrtTermine'
          });
          break;
        case 'ToDo:Wiedervorlagen':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 250,
            id: -2,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 2, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: -1,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:Wiedervorlagen`,
            summary: 'Wiedervorlagen'
          });
          break;
        case 'ToDo:VorOrtTermine':
          filter = Object.assign({}, filter, {
            page: 1,
            pages: 1,
            pageSize: 250,
            id: -2,
            idBusinessPartnerStatus: null,
            businessPartnerSalesPitchStatus: { id: 3, dateStart: null, dateEnd: null },
            idBusinessPartnerOrder: -1,
            order: 'asc',
            signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermine`,
            summary: 'Termin Feedback'
          });
          break;
        case 'LeadList':
          const leadLists = userStore.filterOption.businessPartnerLists.filter((list) => ['Leads', 'Aufgaben'].includes(list.name));
          if (leadLists) {
            filter = Object.assign({}, filter, {
              page: 1,
              pages: 1,
              pageSize: 2500,
              id: -9, // new: not yet viewed
              idBusinessPartnerList: leadLists.map((leadList) => leadList.id).join(','),
              idBusinessPartnerStatus: null,
              businessPartnerSalesPitchStatus: { id: 1, dateStart: null, dateEnd: null },
              idBusinessPartnerOrder: -1,
              signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#LeadList`,
              summary: 'Aktuelle Leads'
            });
          }
          break;
        default:
          break;
      }
      // console.log('namedFilter', name, '=>', filter)
      return filter;
    }
  },
  actions: {
    setLastVisited(partner) {
      if (!partner) return;
      this.lastVisited = {
        id: partner.id || null,
        gpNo: partner.gpNo || null,
        page: partner.__page || null,
        timestamp: Date.now()
      };
      try { sessionStorage.setItem('bp:lastVisited', JSON.stringify(this.lastVisited)); } catch (e) { }
      // update history (max 5 unique by id)
      if (partner.id) {
        const name = partner.name || partner.companyName || '';
        const entry = { id: partner.id, gpNo: partner.gpNo || null, name, page: partner.__page || null, timestamp: Date.now() };
        this.lastVisitedHistory = [entry, ...this.lastVisitedHistory.filter(h => h.id !== entry.id)].slice(0, 5);
        try { sessionStorage.setItem('bp:lastVisitedHistory', JSON.stringify(this.lastVisitedHistory)); } catch (e) { }
      }
    },
    clearLastVisited() {
      this.lastVisited = { id: null, gpNo: null, page: null, timestamp: null };
      try { sessionStorage.removeItem('bp:lastVisited'); } catch (e) { }
    },
    restoreLastVisited() {
      try {
        const raw = sessionStorage.getItem('bp:lastVisited');
        if (raw) {
          const parsed = JSON.parse(raw);
          // optional staleness check (30 min)
          if (parsed?.timestamp && Date.now() - parsed.timestamp < 1000 * 60 * 30) {
            this.lastVisited = parsed;
          } else {
            sessionStorage.removeItem('bp:lastVisited');
          }
        }
        const histRaw = sessionStorage.getItem('bp:lastVisitedHistory');
        if (histRaw) {
          const list = JSON.parse(histRaw);
          if (Array.isArray(list)) this.lastVisitedHistory = list.slice(0, 5);
        }
      } catch (e) { }
    },
    clearHistory() {
      this.lastVisitedHistory = [];
      try { sessionStorage.removeItem('bp:lastVisitedHistory'); } catch (e) { }
    },
    async loadDetailByGpNo(gpNo, preferredId = null) {
      const userStore = useUserStore();
      if (!gpNo) return null;

      const scope = userStore.currentSelection.scope;
      let idSalesAgents = -1;
      let idSalesTeams = -1;
      if (scope === 'salesAgent') {
        idSalesAgents = userStore.currentSelection?.salesAgent?.id ?? -1;
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      } else if (scope === 'salesTeam') {
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      }

      const endpoint = "businessPartners/v1/getBusinessPartner";
      userStore.currentSelection.businessPartner.isLoading = true;
      let response = null;
      try {
        response = await fetchFromBackend(endpoint, {
          id: userStore.id,
          filter: {
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id,
            idSalesAgents,
            idSalesTeams,
            search: {
              mode: 'GPNo',
              string: String(gpNo),
              wholeWord: 0,
            }
          }
        });
      } finally {
        userStore.currentSelection.businessPartner.isLoading = false;
      }

      const responseList = Array.isArray(response?.businessPartnerList)
        ? response.businessPartnerList
        : (Array.isArray(response) ? response : []);

      let detail = responseList.find((bp) => String(bp?.gpNo || bp?.gp?.no || '') === String(gpNo));
      if (!detail && responseList.length > 0) detail = responseList[0];
      if (!detail && response && typeof response === 'object' && (response.id || response.gp)) detail = response;
      if (!detail) return null;

      if (detail?.gp?.no && !detail.gpNo) detail.gpNo = detail.gp.no;
      if (detail?.gp?.name && !detail.name) detail.name = detail.gp.name;

      const resolvedId = detail.id ?? preferredId;
      if (!resolvedId) return detail;

      detail.signature = `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#${resolvedId}`;
      const storedBusinessPartner = this.businessPartners.find((stored) => stored.signature === detail.signature);
      if (!storedBusinessPartner) {
        this.businessPartners.push({
          signature: detail.signature,
          data: detail,
          update: Date.now()
        });
      } else {
        storedBusinessPartner.update = Date.now();
        storedBusinessPartner.data = detail;
      }

      return detail;
    },
    /** start a search with a query, i.e. "Meier" for businessPartners
     * @async
    */
    async getBySearch() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      userStore.currentSelection.businessPartnerList.search.error = null;
      const mode = userStore.currentSelection.businessPartnerList.search.mode;
      const queryString = userStore.currentSelection.businessPartnerList.search.query;
      const wholeWordFlag = userStore.currentSelection.businessPartnerList.search.wholeWord ? 1 : 0;
      // Neues Filter-Payload nach Vorgabe inkl. eingebettetem search-Objekt
      // Scope-abhängige Einschränkung:
      // - campaign: idSalesAgents = -1, idSalesTeams = -1 (kein Agent/Team-Filter)
      // - salesTeam: idSalesTeams = Team-ID, idSalesAgents = -1
      // - salesAgent: beide IDs setzen (Team + Agent)
      const scope = userStore.currentSelection.scope;
      let idSalesAgents = -1;
      let idSalesTeams = -1;
      if (scope === 'salesAgent') {
        idSalesAgents = userStore.currentSelection?.salesAgent?.id ?? -1;
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      } else if (scope === 'salesTeam') {
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      }

      const filter = {
        idCompany: userStore.currentSelection.campaign.company.id,
        idCampaign: userStore.currentSelection.campaign.id,
        idSalesAgents,
        idSalesTeams,
        search: {
          mode: mode,
          string: queryString,
          wholeWord: wholeWordFlag,
        }
      };

      let storedBusinessPartnerList = null;
      const signature = `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#${mode}:${queryString}:${wholeWordFlag}`;
      storedBusinessPartnerList = this.businessPartnerLists.find((businessPartnerList) => businessPartnerList.signature === signature);

      const endpoint = "businessPartners/v1/getBusinessPartner";
      userStore.currentSelection.businessPartner.isLoading = true;
      let response = null;
      try {
        response = await fetchFromBackend(endpoint, {
          id: userStore.id,
          filter: filter
        });
        // Normalisiere Response-Shape: API liefert teils { businessPartnerList: [...] } oder direkt [...]
        let responseList = [];
        if (Array.isArray(response?.businessPartnerList)) {
          responseList = response.businessPartnerList;
        } else if (Array.isArray(response)) {
          responseList = response; // getBusinessPartner kann Array direkt zurückgeben
        }
        // Fallback falls Backend ein einzelnes Objekt liefert
        if (!responseList.length && response && typeof response === 'object' && response.id && response.gp) {
          responseList = [response];
        }
        // Shape-Angleichung: sorge dafür, dass gpNo & name wie in Listenansicht verfügbar sind
        responseList = responseList.map(bp => {
          if (bp?.gp && bp.gp.no && !bp.gpNo) bp.gpNo = bp.gp.no;
          if (bp?.gp && bp.gp.name && !bp.name) bp.name = bp.gp.name;
          return bp;
        });
        response.normalizedList = responseList; // anhängen für spätere Nutzung falls nötig
        console.log('getBusinessPartner: normalized length', responseList.length);
      } catch (error) {
        userStore.currentSelection.businessPartner.isLoading = false;
        notificationStore.addNotification(`Suche fehlgeschlagen! (${error?.message || error})`, 'error');
        return null;
      }
      userStore.currentSelection.businessPartner.isLoading = false;
      const normalizedList = response.normalizedList || [];
      if (!normalizedList.length) {
        notificationStore.addNotification('Kein Suchergebnis gefunden!', 'error');
        // return null;
      }
      userStore.currentSelection.businessPartnerList.search.summary = `Suchergebnis für "${userStore.currentSelection.businessPartnerList.search.query}"`;
      let businessPartnerList = null;
      if (!storedBusinessPartnerList) {
        businessPartnerList = {
          signature: signature,
          summary: `Suchergebnis für "${userStore.currentSelection.businessPartnerList.search.query}"`,
          data: normalizedList,
          size: normalizedList.length,
          update: Date.now()
        }
        this.businessPartnerLists.push(businessPartnerList);
      } else {
        businessPartnerList = Object.assign(storedBusinessPartnerList, {
          data: normalizedList,
          size: normalizedList.length,
          update: Date.now()
        });
      }
      for (const businessPartner of businessPartnerList.data) {
        businessPartner.signature = `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#${businessPartner.id}`;
        // sugarTickets-Feld direkt im Store-Objekt initialisieren, falls nicht vorhanden
        if (!('sugarTickets' in businessPartner)) {
          businessPartner.sugarTickets = [];
        }
        let storedBusinessPartner = this.businessPartners.find((storedBusinessPartner) => storedBusinessPartner.signature === businessPartner.signature);
        if (!storedBusinessPartner) {
          this.businessPartners.push({
            signature: businessPartner.signature,
            data: businessPartner,
            update: Date.now()
          });
        } else {
          storedBusinessPartner.update = Date.now();
          storedBusinessPartner.data = businessPartner;
        }
      }
      userStore.currentSelection.businessPartnerList.signature = signature
      return businessPartnerList
    },
    /** start a search with a filter, i.e. { idBusinessPartnerList: "list:12" } for businessPartners either from Dashboard or from businessPartnerList
     * @async
     * @param {Object} filter - The filter object to search for businessPartners
     * @param {Array} searchCriteria - Additional search criteria to filter the businessPartners
     * @param {Boolean} fromAutoRefresh - If true, the call originated from an auto refrexh like updating the wvl today, set to false to keep the list in the user selection stable (i.e. for the subPage of "potentiale & bk")
    */
    async getByFilter(filter, searchCriteria = [], fromAutoRefresh = false, options = {}) {
      // Special case: always use the initial filter if requested (e.g. from 'zur vollen Liste')
      if (filter === 'initialFilter') {
        // Define the initial filter structure as in your app logic
        const userStore = useUserStore();
        filter = {
          idCompany: userStore.currentSelection.campaign.company.id,
          idCampaign: userStore.currentSelection.campaign.id,
          idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
          idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
          idBusinessPartnerStatus: null,
          idBusinessPartnerList: -1,
          idBusinessPartnerProduct: [],
          idBusinessPartnerProductSegment: [],
          idBusinessPartnerPotential: -1,
          idBusinessPartnerOrder: -1,
          search: { mode: 'name', string: '', wholeWord: 0 },
          page: 1,
          pages: 1,
          pageSize: 24,
          signature: `idBusinessPartnerList:-1|idBusinessPartnerStatus:null|idBusinessPartnerPotential:-1|idBusinessPartnerProduct:[]|idBusinessPartnerProductSegment:[]|idBusinessPartnerCityZip:-1|nameBusinessPartnerSector:|idBusinessPartnerOrder:-1|${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.salesTeam.id}:${userStore.currentSelection.campaign.id}`
        };
      }

      const endpoint = "businessPartners/v1/getListByFilter";
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // FILTER_DEBUG: Log incoming filter param
      console.log('FILTER_DEBUG: getByFilter called with filter', JSON.parse(JSON.stringify(filter)));

      // see if there is existing data if it exists and its update property is less then 6 hours old and it matches the desired page
      const storedBusinessPartnerList = this.businessPartnerLists.find((businessPartnerList) => businessPartnerList.signature === filter.signature);
      // FILTER_DEBUG: Log filter after signature lookup
      console.log('FILTER_DEBUG: getByFilter after signature lookup', JSON.parse(JSON.stringify(filter)));
      if (filter.signature && filter.signature.includes('#Today:')) {
        // console.log('HANUTA:getlistByFilter: checked signature', filter.signature, 'got stored:', storedBusinessPartnerList, 'filter is', filter)
        // console.log(filter.signature)
      }
      // TODO: manage Lists in memory by purging businessPartners from active lists
      // if (storedBusinessPartnerList && storedBusinessPartnerList?.update > (Date.now() - (6 * 60 * 60 * 1000))) return storedBusinessPartnerList;


      // Build effective filter with new nested structures expected by backend
      // Kopiere alle Felder, damit keine UI-Filter verloren gehen
      const f = { ...filter };
      // Sortierrichtung nur in view.sort verwenden, nicht im Filter-Objekt für das Backend
      const sortOrder = filter.order;
      if (Object.prototype.hasOwnProperty.call(f, 'order')) {
        delete f.order;
      }

      // --- Fix: idBusinessPartnerProduct & idBusinessPartnerProductSegment als Array von Objekten (id/include) weiterreichen ---
      // Wenn sie schon Objekte sind, so lassen. Wenn sie Strings sind (Legacy), umwandeln.
      function normalizeIdIncludeArray(arr) {
        if (!Array.isArray(arr)) return [];
        return arr.map(item => {
          if (typeof item === 'object' && item !== null && 'id' in item) return item;
          return { id: item, include: true };
        });
      }
      if (Array.isArray(f.idBusinessPartnerProduct)) {
        f.idBusinessPartnerProduct = normalizeIdIncludeArray(f.idBusinessPartnerProduct);
      }
      if (Array.isArray(f.idBusinessPartnerProductSegment)) {
        f.idBusinessPartnerProductSegment = normalizeIdIncludeArray(f.idBusinessPartnerProductSegment);
      }

      // FILTER_DEBUG: Effective filter before backend mapping (after normalization)
      console.log('FILTER_DEBUG: getByFilter effective filter before mapping', JSON.parse(JSON.stringify(f)));

      // Kontaktstatus (businessPartnerSalesPitchStatus): Wenn als Objekt vorhanden, direkt übernehmen, sonst aus Legacy-Feldern bauen
      if (filter.businessPartnerSalesPitchStatus && typeof filter.businessPartnerSalesPitchStatus === 'object') {
        f.businessPartnerSalesPitchStatus = { ...filter.businessPartnerSalesPitchStatus };
      } else {
        f.businessPartnerSalesPitchStatus = {
          id:
            (filter.idSalesPitchStatus !== undefined && filter.idSalesPitchStatus !== null)
              ? (filter.idSalesPitchStatus !== '' ? String(filter.idSalesPitchStatus) : '-1')
              : '-1',
          dateStart:
            (filter.dateStartSalesPitchStatus !== undefined && filter.dateStartSalesPitchStatus !== null && filter.dateStartSalesPitchStatus !== '')
              ? String(filter.dateStartSalesPitchStatus)
              : null,
          dateEnd:
            (filter.dateEndSalesPitchStatus !== undefined && filter.dateEndSalesPitchStatus !== null && filter.dateEndSalesPitchStatus !== '')
              ? String(filter.dateEndSalesPitchStatus)
              : null,
        };
      }
      delete f.idSalesPitchStatus; delete f.dateStartSalesPitchStatus; delete f.dateEndSalesPitchStatus;

      // Fakturadatum (invoice): immer als Objekt im Filter lassen oder erzeugen
      if (!f.invoice) {
        f.invoice = {};
      }
      if (typeof filter.dateStartInvoice !== 'undefined' || typeof filter.dateEndInvoice !== 'undefined') {
        f.invoice.dateStart = filter.dateStartInvoice ? String(filter.dateStartInvoice) : f.invoice.dateStart || null;
        f.invoice.dateEnd = filter.dateEndInvoice ? String(filter.dateEndInvoice) : f.invoice.dateEnd || null;
        delete f.dateStartInvoice; delete f.dateEndInvoice;
      }

      // Kundenstatus (idBusinessPartnerStatus): immer als String, falls vorhanden
      if (filter.idStatus !== undefined && filter.idStatus !== null) {
        f.idBusinessPartnerStatus = String(filter.idStatus);
        delete f.idStatus;
      } else if (filter.idBusinessPartnerStatus !== undefined && filter.idBusinessPartnerStatus !== null) {
        f.idBusinessPartnerStatus = String(filter.idBusinessPartnerStatus);
      }

      // search: immer als Objekt im Filter lassen oder erzeugen
      if (!f.search) {
        f.search = {};
      }
      if (typeof f.search === 'object') {
        if (typeof f.search.wholeWord === 'boolean') f.search.wholeWord = f.search.wholeWord ? 1 : 0;
        f.search.mode = f.search.mode || 'name';
        f.search.string = f.search.string || '';
        f.search.wholeWord = typeof f.search.wholeWord === 'number' ? f.search.wholeWord : 0;
      }
      const payload = {
        id: userStore.id,
        filter: f,
        view: {
          page: 1,
          pageSize: filter.pageSize * filter.pages,
          searchCriteria: searchCriteria,
          sort: {
            field: filter.idBusinessPartnerOrder,
          }
        },
      }
      if (sortOrder) payload.view.sort.order = sortOrder;
      if (!filter.signature) {
        filter.signature = `fallback:${Date.now()}`;
      }
      // FILTER_DEBUG: Payload before fetchFromBackend
      console.log('FILTER_DEBUG: getByFilter payload for backend', JSON.parse(JSON.stringify(payload)));

      let response = null;
      try {
        // FILTER_DEBUG: About to call fetchFromBackend
        console.log('FILTER_DEBUG: getByFilter calling fetchFromBackend', endpoint, JSON.parse(JSON.stringify(payload)));
        if (filter.signature.includes('#Reminder:') || filter.signature.includes('#ToDo:') || filter.signature.includes('#Today:') || filter.signature.includes('#LeadList')) {
          response = await fetchFromBackend(endpoint, payload);
        } else {
          userStore.currentSelection.businessPartnerList.isLoading = true;
          response = await fetchFromBackend(endpoint, payload);
          userStore.currentSelection.businessPartnerList.isLoading = false;
        }
        // FILTER_DEBUG: Response from fetchFromBackend
        console.log('FILTER_DEBUG: getByFilter response from backend', JSON.parse(JSON.stringify(response)));
      } catch (error) {
        userStore.currentSelection.businessPartnerList.isLoading = false;
        notificationStore.addNotification(`BusinessPartner-Liste konnte nicht geladen werden! (${error?.message || error})`, 'error', 2);
        // FILTER_DEBUG: Error from fetchFromBackend
        console.log('FILTER_DEBUG: getByFilter error from backend', error);
        return false;
      }
      if (!response.businessPartnerList) {
        notificationStore.addNotification('Keine BusinessPartner-Liste gefunden!', 'error', 3);
        return false;
      }
      if (!fromAutoRefresh) {
        userStore.currentSelection.businessPartnerList.signature = filter.signature;
        userStore.currentSelection.businessPartnerList.maxRows = response.view.countBusinessPartnerFiltered;
        userStore.currentSelection.businessPartnerList.page = filter.page;
        // Initialize pages counter for expand logic
        userStore.currentSelection.businessPartnerList.pages = 1;
        // summaryObj für Systemfilter (Reminder, Today, ToDo, LeadList) immer aus dem Backend übernehmen
        const isSystemFilter = /#(Reminder|Today|ToDo|LeadList):?/.test(filter.signature);
        if (isSystemFilter && response.filter && Array.isArray(response.filter.summaryObj)) {
          userStore.currentSelection.businessPartnerList.summaryObj = response.filter.summaryObj;
        } else if (response.filter && Array.isArray(response.filter.summaryObj)) {
          userStore.currentSelection.businessPartnerList.summaryObj = response.filter.summaryObj;
        } else {
          userStore.currentSelection.businessPartnerList.summaryObj = [];
        }
      }

      // if the data does not exists create it, if not update or expand it

      // Hilfsfunktion: Ist es ein Initialfilter?
      function isInitialFilterSignature(signature) {
        if (!signature) return false;
        const mustHaveParts = [
          'idBusinessPartnerStatus:null',
          'idBusinessPartnerList:-1',
          'idBusinessPartnerProduct:[]',
          'idBusinessPartnerProductSegment:[]',
          'idBusinessPartnerPotential:-1',
          'idBusinessPartnerOrder:-1'
        ];
        const idPattern = /\|\d+:\d+:\d+$/;
        if (!mustHaveParts.every(part => signature.includes(part)) || !idPattern.test(signature)) return false;
        return true;
      }

      // Hilfsfunktion: Ist es ein Systemfilter (Reminder, Today, ToDo, LeadList)?
      function isSystemFilterSignature(signature) {
        return /#(Reminder|Today|ToDo|LeadList):?/.test(signature);
      }

      // isUserFilter: true only for explicit user-applied filters, always boolean

      // DEBUG: Log all relevant values for isUserFilter
      // Systemfilter (Reminder, Today, ToDo, LeadList) dürfen NIE isUserFilter:true bekommen
      const systemFilter = isSystemFilterSignature(filter.signature);
      const userApplied = options.userApplied === true;

      // Definiere den Initialfilter analog zu deiner App-Logik (ggf. anpassen)
      const initialFilterObj = {
        idBusinessPartnerStatus: null,
        idBusinessPartnerList: -1,
        idBusinessPartnerProduct: [],
        idBusinessPartnerProductSegment: [],
        idBusinessPartnerPotential: -1,
        idBusinessPartnerOrder: -1,
        // ggf. weitere Felder ergänzen, die im Initialzustand leer/null sind
      };
      function deepEqual(a, b) {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;
        if (typeof a !== 'object' || a === null || b === null) return false;
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const key of aKeys) {
          if (!bKeys.includes(key)) return false;
          if (!deepEqual(a[key], b[key])) return false;
        }
        return true;
      }
      // Prüfe, ob der aktuelle Filter vom Initialfilter abweicht
      const isChanged = !deepEqual(
        Object.fromEntries(Object.entries(filter).filter(([k]) => k in initialFilterObj)),
        initialFilterObj
      );
      // Erweiterte Systemfilter-Erkennung: Auch Filter mit businessPartnerSalesPitchStatus für Today/Reminder
      let isUserFilter;
      // Helper: Prüfe auf Today/Reminder-Filter anhand businessPartnerSalesPitchStatus
      function isTodayOrReminderFilter(f) {
        if (!f || !f.businessPartnerSalesPitchStatus) return false;
        const s = f.businessPartnerSalesPitchStatus;
        // Muss id 2 (WVL Heute) oder 3 (Termine Heute) sein und Datum = heute
        const today = new Date().toISOString().split('T')[0];
        return (
          (s.id === 2 || s.id === 3) &&
          s.dateStart === today &&
          s.dateEnd === today
        );
      }
      if (systemFilter || isTodayOrReminderFilter(filter)) {
        isUserFilter = false;
      } else {
        isUserFilter = (isChanged && userApplied) ? true : false;
      }
      console.log('[BP_FILTER_DEBUG] isUserFilter check:', {
        signature: filter.signature,
        isChanged,
        systemFilter,
        userApplied,
        filter,
        options,
        isUserFilter
      });

      if (!storedBusinessPartnerList) {
        // console.log('HANUTA:getListByFilter, regularList: businessPartnerList was not stored', filter.signature, 'summary:', response.filter.summary)
        const storedFilter = JSON.parse(JSON.stringify(filter));
        const storedSearchCriteria = JSON.parse(JSON.stringify(searchCriteria));
        const storedSummaryObj = Array.isArray(response.filter && response.filter.summaryObj)
          ? JSON.parse(JSON.stringify(response.filter.summaryObj))
          : [];
        this.businessPartnerLists.push({
          signature: filter.signature,
          filter: storedFilter,
          searchCriteria: storedSearchCriteria,
          sort: { id: filter.idBusinessPartnerOrder },
          summary: response.filter.summary,
          summaryObj: storedSummaryObj,
          data: response.businessPartnerList.map((businessPartner) => formatBusinessPartner(businessPartner)),
          size: response.view.countBusinessPartner,
          update: Date.now(),
          isUserFilter
        });
      } else {
        // console.log('HANUTA:getListByFilter, regularList: businessPartnerList was stored', filter.signature)
        storedBusinessPartnerList.update = Date.now();
        storedBusinessPartnerList.summary = response.filter.summary;
        storedBusinessPartnerList.summaryObj = Array.isArray(response.filter && response.filter.summaryObj)
          ? JSON.parse(JSON.stringify(response.filter.summaryObj))
          : [];
        // applied Filter & SearchCriteria vom Backend neu ablegen (entkoppelt vom UI-Filter)
        storedBusinessPartnerList.filter = JSON.parse(JSON.stringify(filter));
        storedBusinessPartnerList.searchCriteria = JSON.parse(JSON.stringify(searchCriteria));
        storedBusinessPartnerList.data = response.businessPartnerList.map((businessPartner) => formatBusinessPartner(businessPartner));
        storedBusinessPartnerList.isUserFilter = isUserFilter;
        // console.log('getListByFilter, regularList: replacing stored businessPartnerList', storedBusinessPartnerList.data.length, 'on', userStore.currentSelection.businessPartnerList.page, userStore.currentSelection.businessPartnerList.pageSize, storedBusinessPartnerList.data.length < (userStore.currentSelection.businessPartnerList.page * userStore.currentSelection.businessPartnerList.pageSize))
      }
    },
    /** use an existing filter to expand a businessPartnerList i.e. { idBusinessPartnerList: "list:12" }
     * @async
    */
    async getByFilterExpand() {
      const endpoint = "businessPartners/v1/getListByFilter";
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // Advance pagination counters on the selection state
      userStore.currentSelection.businessPartnerList.page++;
      userStore.currentSelection.businessPartnerList.pages++;

      // Prefer the currently displayed list's signature; fall back to computed filter signature
      const activeSignature = userStore.currentSelection.businessPartnerList.signature || userStore.currentFilter.filter.signature;
      // Locate the stored list regardless of source (filter or search)
      const storedBusinessPartnerList = this.businessPartnerLists.find((list) => list.signature === activeSignature);
      if (!storedBusinessPartnerList) {
        notificationStore.addNotification('BusinessPartner-Liste zum Erweitern nicht gefunden!', 'error', 4);
        return false;
      }
      // Base filter for expand: use the filter originally used to create the list (ensures matching signature)
      const baseFilter = storedBusinessPartnerList.filter || userStore.currentFilter.filter;
      const filter = Object.assign({}, baseFilter, {
        page: userStore.currentSelection.businessPartnerList.page,
        pages: userStore.currentSelection.businessPartnerList.pages,
        pageSize: userStore.currentSelection.businessPartnerList.pageSize,
        signature: activeSignature,
      });
      const searchCriteria = storedBusinessPartnerList.searchCriteria || userStore.currentFilter.searchCriteria;

      // console.log('getListByFilterExpand: using signature', activeSignature, 'filter', filter)
      // Apply same nested mapping for expand calls
      const f = Object.assign({}, filter);
      // Sortierrichtung nur in view.sort verwenden, nicht im Filter-Objekt für das Backend
      const sortOrder = filter.order;
      if (Object.prototype.hasOwnProperty.call(f, 'order')) {
        delete f.order;
      }
      if (f.idSalesPitchStatus || f.dateStartSalesPitchStatus || f.dateEndSalesPitchStatus) {
        f.businessPartnerSalesPitchStatus = {
          id: f.idSalesPitchStatus ?? null,
          dateStart: f.dateStartSalesPitchStatus ?? null,
          dateEnd: f.dateEndSalesPitchStatus ?? null,
        };
        delete f.idSalesPitchStatus; delete f.dateStartSalesPitchStatus; delete f.dateEndSalesPitchStatus;
      }
      if (f.dateStartInvoice || f.dateEndInvoice) {
        f.invoice = {
          dateStart: f.dateStartInvoice ?? null,
          dateEnd: f.dateEndInvoice ?? null,
        };
        delete f.dateStartInvoice; delete f.dateEndInvoice;
      }
      if (f.idStatus !== undefined && f.idStatus !== null) { f.idBusinessPartnerStatus = f.idStatus; delete f.idStatus; }
      if (f.search && typeof f.search === 'object') {
        if (typeof f.search.wholeWord === 'boolean') f.search.wholeWord = f.search.wholeWord ? 1 : 0;
      }

      const payload = {
        id: userStore.id,
        filter: f,
        view: {
          page: userStore.currentSelection.businessPartnerList.pages,
          pageSize: filter.pageSize,
          searchCriteria: searchCriteria,
          sort: {
            field: filter.idBusinessPartnerOrder,
          }
        },
      }
      if (sortOrder) payload.view.sort.order = sortOrder;
      // if querying a regular businessPartnerList (i.e. by filter) update the main businessPartnerList
      userStore.currentSelection.businessPartnerList.isLoading = true;
      let response = null;
      try {
        response = await fetchFromBackend(endpoint, payload);
        // notificationStore.addNotification(`BusinessPartner-Liste "${filter.summary}" erfolgreich erweitert!`, 'success');
      } catch (error) {
        userStore.currentSelection.businessPartnerList.isLoading = false;
        notificationStore.addNotification(`BusinessPartner-Liste konnte nicht erweitert werden! (${error?.message || error})`, 'error', 5);
        return false;
      }
      userStore.currentSelection.businessPartnerList.isLoading = false;
      userStore.currentSelection.businessPartnerList.maxRows = response.view.countBusinessPartnerFiltered;
      if (!response.businessPartnerList) {
        notificationStore.addNotification('Keine weiteren BusinessPartner gefunden!', 'error', 6);
        return false;
      }
      userStore.currentSelection.businessPartnerList.signature = activeSignature;
      storedBusinessPartnerList.update = Date.now();
      storedBusinessPartnerList.data = storedBusinessPartnerList.data.concat(response.businessPartnerList.map((businessPartner) => formatBusinessPartner(businessPartner)));
    },
    /** at campaign change or in certain intervals, the businessPartnerList data needs to be refreshed to reflect up to date 'Wiedervorlagen', 'vor Ort Termine' and 'Aufgaben' 
     * @async
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // if (userStore.currentSelection.scope !== 'salesAgent') return false;
      const taskList = [
        // this.getByFilter(this.namedFilter('ToDo:Wiedervorlagen')),
        // this.getByFilter(this.namedFilter('ToDo:VorOrtTermine')),
        // this.getByFilter(this.namedFilter('Reminder:Wiedervorlagen')),
        // this.getByFilter(this.namedFilter('Reminder:VorOrtTermine')),
        // this.getByFilter(this.namedFilter('Today:Wiedervorlagen')),
        // this.getByFilter(this.namedFilter('Today:VorOrtTermine')),
        this.getByFilter(userStore.currentFilter.filter, userStore.currentFilter.searchCriteria)
      ];
      userStore.currentSelection.businessPartnerList.isLoading = true;
      try {
        await Promise.all(taskList);
        // notificationStore.addNotification('BusinessPartner-Listen erfolgreich aktualisiert!', 'success');
      } catch (error) {
        notificationStore.addNotification(`BusinessPartner-Listen-Refresh fehlgeschlagen! (${error?.message || error})`, 'error', 7);
      }
      userStore.currentSelection.businessPartnerList.isLoading = false;
    },
    /** add a salesPitch to the businessPartner
     * @async
    */
    async saveSalesPitch(salesPitch) {
      const userStore = useUserStore();
      const activityStore = useActivityStore();
      const notificationStore = useNotificationStore();
      // if no salesPitch was added, return false, else save the new salesPitch
      const hasQueue = Array.isArray(userStore.currentSelection.businessPartner?.additionalSalesPitches) && userStore.currentSelection.businessPartner.additionalSalesPitches.length > 0;
      const additionalSalesPitch = salesPitch || (hasQueue ? userStore.currentSelection.businessPartner.additionalSalesPitches[userStore.currentSelection.businessPartner.additionalSalesPitches.length - 1] : null);
      if (!additionalSalesPitch || !additionalSalesPitch.offerList) return false;
      // normalize comment to non-null string
      additionalSalesPitch.comment = String(additionalSalesPitch.comment ?? '').trim();
      normalizeOfferListProductIds(additionalSalesPitch.offerList);
      // ensure salesPitch carries a Sugar stub as required by backend
      if (!additionalSalesPitch.sugar || typeof additionalSalesPitch.sugar !== 'object') {
        additionalSalesPitch.sugar = { id: null };
      } else if (!('id' in additionalSalesPitch.sugar)) {
        additionalSalesPitch.sugar.id = null;
      }
      // backend needs the campaign as an object with id
      additionalSalesPitch.campaign = { id: userStore.currentSelection.campaign.id };
      // send the the data to the backend
      const endpoint = "businessPartners/v1/addSalesPitch";
      userStore.currentSelection.businessPartner.isLoading = true;
      try {
        // Backend contract: only { user:{id}, businessPartner:{id,lists}, salesPitch }.
        const payload = {
          salesAgent: {
            id: userStore.currentSelection.salesAgent?.id,
            idUser: userStore.id,
            firstName: userStore.currentSelection.salesAgent?.firstName,
            lastName: userStore.currentSelection.salesAgent?.lastName,
            emailAdress: userStore.currentSelection.salesAgent?.emailAdress,
            phone: userStore.currentSelection.salesAgent?.phone,
            sugar: {
              id: userStore.sugar.id || null,
              source: userStore.sugar.source || null
            }
          },
          businessPartner: pickSalesPitchBusinessPartnerFields(this.businessPartner),
          salesPitch: {
            ...additionalSalesPitch,
            contact: pickSalesPitchContactFields(additionalSalesPitch.contact),
            opportunities: Array.isArray(this.businessPartner.lists) ? this.businessPartner.lists.map((list) => ({ id: list.id, name: list.name, colleaguesWithAccess: list.colleaguesWithAccess, statusClosed: list.statusClosed })) : [],
          },
        };
        const response = await fetchFromBackend(endpoint, payload);

        if (!response) {
          userStore.currentSelection.businessPartner.isLoading = false;
          notificationStore.addNotification('Speichern fehlgeschlagen! Der Server hat den Sales Pitch nicht angelegt.', 'error', 12);
          return false;
        }

        notificationStore.addNotification('Speichern erfolgreich!', 'success');

        try {
          await activityStore.refreshMode('week');
          userStore.currentSelection.businessPartner.isLoading = false;
          // notificationStore.addNotification('Wochendaten erfolgreich aktualisiert!', 'success');
          return response;
        } catch (error) {
          userStore.currentSelection.businessPartner.isLoading = false;
          notificationStore.addNotification(`Aktualisierung der Wochendaten fehlgeschlagen! (${error?.message || error})`, 'error', 8);
          return false;
        }
      } catch (error) {
        userStore.currentSelection.businessPartner.isLoading = false;
        notificationStore.addNotification(`Speichern fehlgeschlagen! (${error?.message || error})`, 'error', 9);
        return false;
      }
    },
    /** saves the feedback of a sales appointment by either closing or rescheduling the appointment
     * @async
    */
    async saveFeedback(businessPartner) {
      const userStore = useUserStore();
      const activityStore = useActivityStore();
      const notificationStore = useNotificationStore();
      // if no salesPitch exists, return false, else save the new salesPitch
      if (businessPartner.salesPitchList.length === 0) return false;
      const lastSalesPitch = businessPartner.salesPitchList[businessPartner.salesPitchList.length - 1]
      normalizeOfferListProductIds(lastSalesPitch.offerList);
      // backend needs the campaign as an object with id
      lastSalesPitch.campaign = { id: userStore.currentSelection.campaign.id };

      // fetch the data from the backend
      const endpoint = "businessPartners/v1/addSalesPitch";
      userStore.currentSelection.businessPartner.isLoading = true;
      let response = null;
      try {
        // Backend contract: only { user:{id}, businessPartner:{id,lists}, salesPitch }.
        response = await fetchFromBackend(endpoint, {
          salesAgent: {
            id: userStore.currentSelection.salesAgent?.id,
            idUser: userStore.id,
            firstName: userStore.currentSelection.salesAgent?.firstName,
            lastName: userStore.currentSelection.salesAgent?.lastName,
            emailAdress: userStore.currentSelection.salesAgent?.emailAdress,
            phone: userStore.currentSelection.salesAgent?.phone,
            sugar: {
              id: userStore.sugar.id || null,
              source: userStore.sugar.source || null
            }
          },
          businessPartner: pickSalesPitchBusinessPartnerFields(businessPartner),
          salesPitch: {
            ...lastSalesPitch,
            contact: pickSalesPitchContactFields(lastSalesPitch.contact),
            // the backend picks the task to update from colleaguesWithAccess (determineActiveTask); without it every feedback created a new SUGAR task
            opportunities: Array.isArray(businessPartner.lists) ? businessPartner.lists.map((list) => ({ id: list.id, name: list.name, colleaguesWithAccess: list.colleaguesWithAccess, statusClosed: list.statusClosed })) : [],
          },
        });

        if (!response) {
          userStore.currentSelection.businessPartner.isLoading = false;
          notificationStore.addNotification('Feedback speichern fehlgeschlagen! Der Server hat das Feedback nicht angelegt.', 'error', 13);
          return false;
        }

        await activityStore.refreshMode('week');
        userStore.currentSelection.businessPartner.isLoading = false;
        notificationStore.addNotification('Feedback erfolgreich gespeichert!', 'success');
        return (response === true) ? true : false;
      } catch (error) {
        userStore.currentSelection.businessPartner.isLoading = false;
        notificationStore.addNotification(`Feedback speichern fehlgeschlagen! (${error?.message || error})`, 'error', 10);
        return false;
      }
    },
    /** reschedule a salesPitch of type "Wiedervorlage" of a businessPartner
     * @async
     * @param {Object} salesPitch - The salesPitch to reschedule
     * @return {Boolean} - True if the rescheduling was successful, false otherwise
    */
    async rescheduleSalesPitch(salesPitch) {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // fetch the data from the backend
      const endpoint = "businessPartners/v1/rescheduleSalesPitch";
      try {
        userStore.currentSelection.businessPartner.isLoading = true;
        await fetchFromBackend(endpoint, {
          id: userStore.id,
          salesPitch: {
            id: salesPitch.id,
            dateFollowUp: `${salesPitch.rescheduleDate}T${(salesPitch.rescheduleTime) ? salesPitch.rescheduleTime : '00:00'}:00.000Z`,
          }
        });
        userStore.currentSelection.businessPartner.isLoading = false;
        // if the rescheduling succeeded, remove the entry from the 'Wiedervorlagen' list
        const businessPartnerList = this.businessPartnerLists.find((list) => list.signature === `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:Wiedervorlagen`);
        // console.log('removing salePitch businessPartner', salesPitch.businessPartner.id, 'from list', businessPartnerList)
        if (businessPartnerList) {
          businessPartnerList.data = businessPartnerList.data.filter((businessPartner) => businessPartner.id !== salesPitch.businessPartner.id);
        }
        notificationStore.addNotification('Wiedervorlage erfolgreich verschoben!', 'success');
        return true
      } catch (error) {
        userStore.currentSelection.businessPartner.isLoading = false;
        notificationStore.addNotification(`Wiedervorlage konnte nicht verschoben werden! (${error?.message || error})`, 'error', 11);
        return false;
      }
    },
    /** after the salesPitch state of a businessPartner was change it is possible that the businessPartner needs to be removed from various lists
     * @param {Object} businessPartner - The businessPartner that was changed 
    */
    pruneBusinessPartnerLists(businessPartner) {
      // TODO: loop thrue all lists to see if the businessPartner
      this.businessPartnerLists = [];
    },

    async saveContact(contact) {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();

      const body = {
        id: userStore.id || null,
        businessPartnerContact: {
          idBusinessPartner: contact.idBusinessPartner || userStore.currentSelection.businessPartner.id,
          gender: contact.gender,
          firstName: contact.firstName || null,
          lastName: contact.lastName || null,
          phoneNumbers: [],
          emailAddresses: []
        }
      };

      // Telefonnumer(n) hinzufügen
      if (contact.phoneStationary) {
        body.businessPartnerContact.phoneNumbers.push({ type: "work", name: contact.phoneStationary });
      }
      if (contact.phoneMobile) {
        body.businessPartnerContact.phoneNumbers.push({ type: "mobile", name: contact.phoneMobile });
      }

      // E-Mail(s) hinzufügen
      if (contact.email) {
        body.businessPartnerContact.emailAddresses.push(contact.email);
      }

      try {
        const response = await fetchFromBackend("businessPartners/v1/addContact", body);
        if (response.errors?.length > 0) {
          notificationStore.addNotification(`Entscheider speichern fehlgeschlagen! (${response.errors})`, 'error');
          return null;
        }
        notificationStore.addNotification('Entscheider erfolgreich gespeichert!', 'success');
        return response;

      } catch (error) {

        notificationStore.addNotification(`Entscheider speichern fehlgeschlagen! (${error?.message || error})`, 'error');
        return null;

      }
    },
    /**
     * Aktualisiert einen bestehenden Entscheider-Kontakt
     * @param {Object} contact - Kontaktobjekt (muss contact.id enthalten)
     * @returns {Object|null}
     */
    async updateContact(contact) {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      if (!contact.id) {
        notificationStore.addNotification('Kontakt kann nicht aktualisiert werden: fehlende ID', 'error');
        return null;
      }
      const body = {
        id: userStore.id || null,
        businessPartnerContact: {
          id: contact.id,
          idBusinessPartner: contact.idBusinessPartner || userStore.currentSelection.businessPartner.id,
          gender: contact.gender,
          firstName: contact.firstName || null,
          lastName: contact.lastName || null,
          phoneNumbers: [],
          emailAddresses: []
        }
      };

      if (contact.phoneStationary) body.businessPartnerContact.phoneNumbers.push({ type: 'work', name: contact.phoneStationary });
      if (contact.phoneMobile) body.businessPartnerContact.phoneNumbers.push({ type: 'mobile', name: contact.phoneMobile });
      if (contact.email) body.businessPartnerContact.emailAddresses.push(contact.email);

      try {
        const response = await fetchFromBackend('businessPartners/v1/updateContact', body);
        notificationStore.addNotification('Entscheider erfolgreich aktualisiert!', 'success');
        return response;
      } catch (error) {
        notificationStore.addNotification(`Entscheider aktualisieren fehlgeschlagen! (${error?.message || error})`, 'error');
        return null;
      }
    },
  },
});

/** Picks only the fields addSalesPitch needs off a businessPartner, used by both
 * saveSalesPitch and saveFeedback (both call the same endpoint) — just enough
 * for the backend to identify it and check list access, lists reduced to their id.
 * @param {Object} businessPartner
 * @return {Object}
 */
function pickSalesPitchBusinessPartnerFields(businessPartner) {
  return {
    id: businessPartner.id,
    // TODO: no real salesStatus field on businessPartner yet — hardcode "BK" until the backend provides one
    salesStatus: businessPartner.salesStatus,
    gpNo: businessPartner.gp?.no ?? null,
    name: businessPartner.gp?.name ?? null,
    sugar: { id: businessPartner.idSugar ?? null },
    businessCategories: Array.isArray(businessPartner.businessCategoryList) ? businessPartner.businessCategoryList : [],
    address: businessPartner.addressList?.[0] ?? null,
    tour: businessPartner.tour ?? null,
  };
}

/** Picks only the fields addSalesPitch needs off a salesPitch's contact — the full
 * contact carries dateCreated, emailList, phoneStationary/-Mobile, source, etc.,
 * which the backend rejects with a 422.
 * @param {Object|null} contact
 * @return {Object|null}
 */
function pickSalesPitchContactFields(contact) {
  if (!contact) return null;
  return {
    id: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    phoneNumbers: Array.isArray(contact.phoneNumbers) && contact.phoneNumbers.length > 0
      ? [contact.phoneNumbers[0]]
      : [],
    emailAddresses: Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0
      ? [contact.emailAddresses[0]]
      : [],
    sugar: { id: contact.sugar?.id ?? null },
  };
}

/** Normalizes offer.product.id values that may arrive prefixed with "product:" from filterOptions.
 * @param {Array} offerList
 */
function normalizeOfferListProductIds(offerList) {
  for (const offer of offerList || []) {
    const rawId = offer?.product?.id;
    const normalizedId =
      typeof rawId === 'string'
        ? parseInt(rawId.replace('product:', ''))
        : (typeof rawId === 'number' ? rawId : NaN);
    if (!Number.isNaN(normalizedId)) offer.product.id = normalizedId;
  }
}

/** a helper function to format/ expand the businessPartner data bejond the data that is returned from the backend
 * @param {Object} businessPartner
 * @return {Object} - The expanded businessPartner data
 */
function formatBusinessPartner(businessPartner) {
  const userStore = useUserStore();
  // Erzeuge eine tiefe Kopie, mutiere niemals das Originalobjekt!
  const businessPartnerCopy = JSON.parse(JSON.stringify(businessPartner));
  businessPartnerCopy.name = businessPartnerCopy.name || businessPartnerCopy.companyName || businessPartnerCopy.gp?.name || '';
  // Initialisiere sugarTickets-Feld für Tickets aus Sugar
  businessPartnerCopy.sugarTickets = [];
  // Normalisiere unterschiedliche Backend-Felder für Vertragsdokumente
  if (!businessPartnerCopy.orderDocuments && Array.isArray(businessPartnerCopy.contractDocuments)) {
    businessPartnerCopy.orderDocuments = businessPartnerCopy.contractDocuments.map(cd => ({
      orderNo: cd.orderNo,
      orderDocuments: Array.isArray(cd.orderDocuments) ? cd.orderDocuments : []
    }));
  }
  // Initialisiere Felder
  businessPartnerCopy.products = [];
  businessPartnerCopy.productGroups = [];
  businessPartnerCopy.contact = {};
  businessPartnerCopy.offerList = [];
  // Umsatzfelder aus unterschiedlichen Backend-Shapes robust normalisieren
  const salesValuesSource = (businessPartnerCopy.salesValues && typeof businessPartnerCopy.salesValues === 'object')
    ? businessPartnerCopy.salesValues
    : ((businessPartnerCopy.salesValue && typeof businessPartnerCopy.salesValue === 'object') ? businessPartnerCopy.salesValue : {});
  const toNumberOrZero = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0;
  };
  businessPartnerCopy.salesValues = {
    total: toNumberOrZero(salesValuesSource.total ?? salesValuesSource.Total ?? businessPartnerCopy.salesValue_total),
    omp: toNumberOrZero(salesValuesSource.omp ?? salesValuesSource.OMP ?? businessPartnerCopy.salesValue_OMP),
    oe: toNumberOrZero(salesValuesSource.oe ?? salesValuesSource.OE ?? businessPartnerCopy.salesValue_OE),
    tb: toNumberOrZero(salesValuesSource.tb ?? salesValuesSource.TB ?? businessPartnerCopy.salesValue_TB),
    gs: toNumberOrZero(salesValuesSource.gs ?? salesValuesSource.GS ?? businessPartnerCopy.salesValue_GS),
  };
  // Normalize revenue field for consistent UI usage
  businessPartnerCopy.totalRevenue = (
    businessPartnerCopy.totalRevenue ??
    businessPartnerCopy.sumRevenue ??
    businessPartnerCopy.revenueTotal ??
    businessPartnerCopy.salesValueTotal ??
    businessPartnerCopy.salesValues.total ??
    businessPartnerCopy.revenue ??
    null
  );
  // Produkte gruppieren
  for (const contract of businessPartnerCopy.contractList || []) {
    const signature = `${contract.product.id}:${(contract.product.isOMP) ? 'OMP' : contract.publication.split(' ')[0]}`;
    let productExists = businessPartnerCopy.products.find((product) => product.signature === signature);
    if (!productExists) {
      productExists = {
        id: contract.product.id,
        signature: signature,
        name: contract.product.name,
        listAs: contract.product.listAs,
        sortOrder: contract.product.sortOrder,
        productGroup: { name: null, sortOrder: 0, reportingLink: null },
        contracts: []
      }
      if (contract.product.isOMP) {
        productExists.productGroup.name = 'OMP';
      } else {
        productExists.productGroup.name = contract.publication.split(' ')[0];
      }
      businessPartnerCopy.products.push(productExists);
    }
    productExists.contracts.push(contract);
  }
  for (const product of businessPartnerCopy.products) {
    product.contracts.sort((a, b) => parseInt(b.dates.end) - parseInt(a.dates.end));
    if (product.contracts[0].dates.end < userStore.currentSelection.campaign.dateStart) {
      product.status = 'history';
      continue;
    }
    if (['green'].includes(product.contracts[0].status.signal?.trim())) {
      product.status = 'active';
      continue;
    }
    product.status = 'terminated';
  }
  businessPartnerCopy.products = businessPartnerCopy.products.sort((a, b) => a.sortOrder - b.sortOrder);
  // KST Links
  if (businessPartnerCopy.gp && businessPartnerCopy.gp.no && Array.isArray(businessPartnerCopy.contractList)) {
    businessPartnerCopy.kstLinks = generateKSTLinks(businessPartnerCopy.contractList, businessPartnerCopy.gp.no);
    if (businessPartnerCopy.kstLinks.length > 0) {
      businessPartnerCopy.reportingLink = `https://reporting.mon.coffee/KSTReporting/performanceMonthly?gpNo=${businessPartnerCopy.gp.no}`;
    } else {
      businessPartnerCopy.reportingLink = undefined;
    }
  } else {
    businessPartnerCopy.kstLinks = [];
    businessPartnerCopy.reportingLink = undefined;
  }
  // SalesPitch
  if (businessPartnerCopy.salesPitch) {
    const salesPitch = businessPartnerCopy.salesPitch;
    // Ohne Potential liefert die Liste offerList: null; Geschäftspartner und salesPitch müssen trotzdem dasselbe Array teilen,
    // sonst landen im Termin-Feedback hinzugefügte Potentiale in einer Liste, die weder angezeigt noch gespeichert wird
    if (!Array.isArray(salesPitch.offerList)) salesPitch.offerList = [];
    businessPartnerCopy.offerList = salesPitch.offerList;
    businessPartnerCopy.status = (salesPitch.status) ? salesPitch.status : null;
  }
  return businessPartnerCopy;
}
/**
 * Generates KST links for salesMatrix based on contractList and gpNo.
 * Each link includes group.context if available.
 * @param {Array} contractList - List of contracts
 * @param {string|number} gpNo - Business partner number
 * @returns {Array} - Array of KST link objects
 */
function generateKSTLinks(contractList, gpNo) {
  if (!Array.isArray(contractList) || !gpNo) return [];
  // Group contracts by publication context
  const groups = {};
  for (const contract of contractList) {
    const context = contract.publication?.split(' ')[0] || 'Unknown';
    if (!groups[context]) {
      groups[context] = {
        context,
        contracts: [],
        groupContext: contract.group?.context || undefined
      };
    }
    groups[context].contracts.push(contract);
  }
  // Build KST link objects
  const links = Object.values(groups).map(group => ({
    context: group.context,
    href: gpNo
      ? `https://reporting.mon.coffee/KSTReporting/performanceMonthly?gpNo=${gpNo}&context=${group.context === 'OE' ? 'OETB' : group.context}`
      : '#',
    contracts: group.contracts,
    groupContext: group.groupContext
  }));

  // Gesamt-Link: falls mindestens ein OE, TB oder GS existiert
  const hasAny = ['OE', 'TB', 'GS'].some(ctx => groups[ctx]);
  if (hasAny) {
    links.push({
      context: 'ALL',
      href: gpNo ? `https://reporting.mon.coffee/KSTReporting/performanceMonthly?gpNo=${gpNo}` : '#',
      contracts: [].concat(
        ...(groups['OE'] ? groups['OE'].contracts : []),
        ...(groups['TB'] ? groups['TB'].contracts : []),
        ...(groups['GS'] ? groups['GS'].contracts : [])
      ),
      groupContext: undefined
    });
  }
  return links;
}